import { Fragment, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
  ResponsiveContainer, Legend,
} from "recharts";
import apiClient from "../api/client";

// ── Constants ─────────────────────────────────────────────────────────────────
const BLUE  = "#2563EB";
const AMBER = "#D97706";
const GREEN = "#059669";

const TABS = [
  { id: "overview",   label: "Overview",   icon: "📊" },
  { id: "leads",      label: "Leads",       icon: "🧲" },
  { id: "resources",  label: "Resources",   icon: "📄" },
  { id: "users",      label: "Users",       icon: "👥" },
  { id: "workbooks",  label: "Workbooks",   icon: "📚" },
  { id: "feedback",   label: "Feedback",    icon: "💬" },
  { id: "surveys",    label: "Surveys",     icon: "📋" },
];

// ── Shared style tokens ───────────────────────────────────────────────────────
const card = {
  background: "#fff",
  border: "1px solid #e8e0d0",
  borderRadius: 8,
  padding: "1.5rem",
};

const sectionHeading = {
  fontFamily: "'Fraunces', serif",
  fontSize: "1.05rem",
  fontWeight: 700,
  color: "#1a1a1a",
  margin: "0 0 1rem 0",
};

const thStyle = {
  padding: "10px 12px",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  color: "#6B7280",
  textTransform: "uppercase",
  letterSpacing: ".05em",
  borderBottom: "1px solid #e8e0d0",
  whiteSpace: "nowrap",
  background: "#faf8f5",
};

const td = (alt = false) => ({
  padding: "10px 12px",
  fontSize: 13,
  color: "#374151",
  background: alt ? "#faf8f5" : "#fff",
  verticalAlign: "middle",
});

// ── Shared helpers ────────────────────────────────────────────────────────────
const Spinner = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
    <style>{`@keyframes adm-spin { to { transform: rotate(360deg); } }`}</style>
    <div style={{
      width: 38, height: 38, borderRadius: "50%",
      border: "3px solid #e8e0d0", borderTopColor: BLUE,
      animation: "adm-spin 0.8s linear infinite",
    }} />
  </div>
);

const Stars = ({ value = 0 }) => (
  <span aria-label={`${value} stars`}>
    {[1, 2, 3, 4, 5].map(n => (
      <span key={n} style={{ color: n <= value ? AMBER : "#D1D5DB", fontSize: 15 }}>★</span>
    ))}
  </span>
);

const TierBadge = ({ paid }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
    background: paid ? "#ECFDF5" : "#F3F4F6",
    color: paid ? GREEN : "#6B7280",
    border: `1px solid ${paid ? "#BBF7D0" : "#E5E7EB"}`,
  }}>
    {paid ? "Paid" : "Free"}
  </span>
);

const DiffBadge = ({ value }) => {
  const map = {
    too_easy:   { label: "Too Easy",   bg: "#FEF3C7", color: "#92400E", border: "#FCD34D" },
    just_right: { label: "Just Right", bg: "#ECFDF5", color: "#065F46", border: "#BBF7D0" },
    too_hard:   { label: "Too Hard",   bg: "#FEE2E2", color: "#991B1B", border: "#FECACA" },
  };
  const s = map[value] || { label: value || "—", bg: "#F3F4F6", color: "#374151", border: "#E5E7EB" };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {s.label}
    </span>
  );
};

const EmptyRow = ({ cols, text = "No data" }) => (
  <tr>
    <td colSpan={cols} style={{ padding: "2rem", textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>
      {text}
    </td>
  </tr>
);

// ── Overview Tab ──────────────────────────────────────────────────────────────
const KpiCard = ({ label, value }) => (
  <div style={{ ...card, textAlign: "center" }}>
    <div style={{
      fontSize: "1.8rem", fontWeight: 700,
      fontFamily: "'Fraunces', serif", color: "#1a1a1a", lineHeight: 1.1,
    }}>
      {value ?? "—"}
    </div>
    <div style={{ fontSize: 12, color: "#6B7280", marginTop: 6, lineHeight: 1.4 }}>{label}</div>
  </div>
);

const OverviewTab = ({ data }) => {
  const kpis = [
    { label: "Total Users",            value: data.kpis?.totalUsers },
    { label: "Paid Users",             value: data.kpis?.paidUsers },
    { label: "Free Users",             value: data.kpis?.freeUsers },
    { label: "Conversion Rate",        value: data.kpis?.conversionRate != null ? `${Number(data.kpis?.conversionRate).toFixed(1)}%` : "—" },
    { label: "Total Workbooks",        value: data.kpis?.totalWorkbooks },
    { label: "Total Feedbacks",        value: data.kpis?.totalFeedbacks },
    { label: "Avg Clarity Rating",     value: data.kpis?.avgClarity != null ? Number(data.kpis?.avgClarity).toFixed(2) : "—" },
    { label: "Avg Purchase Likelihood",value: data.kpis?.avgLikelihood != null ? Number(data.kpis?.avgLikelihood).toFixed(1) : "—" },
  ];

  const tierPie = [
    { name: "Free", value: data.kpis?.freeUsers || 0 },
    { name: "Paid", value: data.kpis?.paidUsers || 0 },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* KPI Grid */}
      <div>
        <h2 style={sectionHeading}>Key Metrics</h2>
        <div className="adm-kpi-grid">
          {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
        </div>
      </div>

      {/* Charts row */}
      <div className="adm-two-col">
        <div style={card}>
          <h3 style={sectionHeading}>Users per Week</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.usersPerWeek || []} margin={{ top: 4, right: 8, bottom: 4, left: -16 }}>
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill={BLUE} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={card}>
          <h3 style={sectionHeading}>User Tier Split</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={tierPie} cx="50%" cy="50%" outerRadius={78} dataKey="value" label>
                <Cell fill={AMBER} />
                <Cell fill={BLUE} />
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Workbook stats table */}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "1.5rem 1.5rem 0.75rem" }}>
          <h3 style={{ ...sectionHeading, margin: 0 }}>Workbook Stats</h3>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Title", "Sections", "Active Users", "Avg Completion %"].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.workbookStats || []).length === 0
                ? <EmptyRow cols={4} />
                : (data.workbookStats || []).map((row, i) => (
                  <tr key={i}>
                    <td style={td(i % 2 === 1)}>{row.title || "—"}</td>
                    <td style={td(i % 2 === 1)}>{row.sections ?? "—"}</td>
                    <td style={td(i % 2 === 1)}>{row.activeUsers ?? "—"}</td>
                    <td style={td(i % 2 === 1)}>
                      {row.avgCompletion != null ? `${Number(row.avgCompletion).toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Users Tab ─────────────────────────────────────────────────────────────────
const UsersTab = ({ users = [] }) => {
  const [search, setSearch]       = useState("");
  const [expanded, setExpanded]   = useState(null);
  const [detailMap, setDetailMap] = useState({});
  const [loadingId, setLoadingId] = useState(null);

  const filtered = users.filter(u =>
    (u.name  || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(search.toLowerCase())
  );

  const toggleRow = async (user) => {
    if (expanded === user.id) { setExpanded(null); return; }
    setExpanded(user.id);
    if (detailMap[user.id] !== undefined) return;
    setLoadingId(user.id);
    try {
      const detail = await apiClient.get(`/api/admin/user/${user.id}`, { authRequired: true });
      setDetailMap(prev => ({ ...prev, [user.id]: detail }));
    } catch {
      setDetailMap(prev => ({ ...prev, [user.id]: null }));
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <input
        type="text"
        placeholder="Search by name or email…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          width: "100%", maxWidth: 360, padding: "9px 14px",
          border: "1px solid #e8e0d0", borderRadius: 8, fontSize: 14,
          fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box",
          background: "#fff",
        }}
      />

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Name", "Email", "Tier", "Joined", "Completed Sections", "Feedbacks", "Survey"].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0
                ? <EmptyRow cols={7} text={search ? "No users match your search" : "No users"} />
                : filtered.map((u, i) => (
                  <Fragment key={u.id}>
                    <tr
                      className="adm-user-row"
                      onClick={() => toggleRow(u)}
                      style={{ cursor: "pointer" }}
                    >
                      <td style={td(i % 2 === 1)}>{u.name || "—"}</td>
                      <td style={td(i % 2 === 1)}>{u.email}</td>
                      <td style={td(i % 2 === 1)}><TierBadge paid={u.paid} /></td>
                      <td style={td(i % 2 === 1)}>
                        {u.joinedDate ? new Date(u.joinedDate).toLocaleDateString() : "—"}
                      </td>
                      <td style={td(i % 2 === 1)}>{u.completedSections ?? "—"}</td>
                      <td style={td(i % 2 === 1)}>{u.feedbackCount ?? "—"}</td>
                      <td style={td(i % 2 === 1)}>
                        {u.surveySubmitted
                          ? <span style={{ color: GREEN, fontWeight: 600 }}>✓</span>
                          : <span style={{ color: "#D1D5DB" }}>—</span>}
                      </td>
                    </tr>

                    {expanded === u.id && (
                      <tr>
                        <td colSpan={7} style={{
                          padding: "1rem 1.5rem",
                          background: "#F9FAFB",
                          borderBottom: "1px solid #e8e0d0",
                        }}>
                          {loadingId === u.id ? (
                            <span style={{ fontSize: 13, color: "#6B7280" }}>Loading…</span>
                          ) : detailMap[u.id] == null ? (
                            <span style={{ fontSize: 13, color: "#EF4444" }}>Failed to load details</span>
                          ) : (
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: "#1a1a1a" }}>
                                Progress Details
                              </div>
                              <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                  <thead>
                                    <tr>
                                      {["Section", "Workbook", "Completed", "Time Spent"].map(h => (
                                        <th key={h} style={{ ...thStyle, background: "#F3F4F6" }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(detailMap[u.id].progress || []).length === 0
                                      ? <EmptyRow cols={4} text="No progress recorded" />
                                      : (detailMap[u.id].progress || []).map((p, j) => (
                                        <tr key={j}>
                                          <td style={td(j % 2 === 1)}>{p.sectionTitle || "—"}</td>
                                          <td style={td(j % 2 === 1)}>{p.workbookTitle || "—"}</td>
                                          <td style={td(j % 2 === 1)}>
                                            {p.completed
                                              ? <span style={{ color: GREEN }}>✓</span>
                                              : <span style={{ color: "#D1D5DB" }}>—</span>}
                                          </td>
                                          <td style={td(j % 2 === 1)}>
                                            {p.timeSpent != null ? `${p.timeSpent}s` : "—"}
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Workbooks Tab ─────────────────────────────────────────────────────────────
const WorkbooksTab = ({ data }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
    {/* Workbook cards */}
    <div>
      <h2 style={sectionHeading}>Workbooks</h2>
      <div className="adm-wb-grid">
        {(data.workbookStats || []).map((wb, i) => (
          <div key={i} style={card}>
            <div style={{
              fontFamily: "'Fraunces', serif", fontWeight: 700,
              fontSize: "1rem", marginBottom: 12, color: "#1a1a1a",
            }}>
              {wb.title}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#6B7280" }}>Active Users</span>
                <span style={{ fontWeight: 600 }}>{wb.activeUsers ?? "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#6B7280" }}>Avg Completion</span>
                <span style={{ fontWeight: 600, color: BLUE }}>
                  {wb.avgCompletion != null ? `${Number(wb.avgCompletion).toFixed(1)}%` : "—"}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Section funnel table */}
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "1.5rem 1.5rem 0.75rem" }}>
        <h3 style={{ ...sectionHeading, margin: 0 }}>Section Funnel</h3>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["Section Title", "Workbook", "Unique Views", "Completions", "Avg Time (s)"].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.sectionFunnel || []).length === 0
              ? <EmptyRow cols={5} />
              : (data.sectionFunnel || []).map((row, i) => (
                <tr key={i}>
                  <td style={td(i % 2 === 1)}>{row.title || "—"}</td>
                  <td style={td(i % 2 === 1)}>{row.workbookTitle || "—"}</td>
                  <td style={td(i % 2 === 1)}>{row.uniqueViews ?? "—"}</td>
                  <td style={td(i % 2 === 1)}>{row.completions ?? "—"}</td>
                  <td style={td(i % 2 === 1)}>{row.avgSeconds ?? "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Clarity per section chart */}
    {(data.clarityPerSection || []).length > 0 && (
      <div style={card}>
        <h3 style={sectionHeading}>Clarity per Section</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={data.clarityPerSection}
            margin={{ top: 4, right: 8, bottom: 44, left: -16 }}
          >
            <XAxis
              dataKey="section"
              tick={{ fontSize: 10 }}
              angle={-30}
              textAnchor="end"
              interval={0}
            />
            <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="avgClarity" fill={AMBER} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);

// ── Feedback Tab ──────────────────────────────────────────────────────────────
const DIFF_COLORS = { "Too Easy": AMBER, "Just Right": GREEN, "Too Hard": "#EF4444" };

const FeedbackTab = ({ data }) => {
  const [filter, setFilter] = useState("all");

  const all      = data.feedbacks || [];
  const filtered = filter === "all" ? all : all.filter(f => f.difficulty === filter);
  const diffPie  = data.difficultyBreakdown || [];

  const filterBtns = [
    { id: "all",        label: "All" },
    { id: "too_easy",   label: "Too Easy" },
    { id: "just_right", label: "Just Right" },
    { id: "too_hard",   label: "Too Hard" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Filter buttons */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {filterBtns.map(b => (
          <button
            key={b.id}
            onClick={() => setFilter(b.id)}
            style={{
              padding: "6px 14px", borderRadius: 99, fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
              border: filter === b.id ? `1.5px solid ${BLUE}` : "1.5px solid #e8e0d0",
              background: filter === b.id ? "#EFF6FF" : "#fff",
              color: filter === b.id ? BLUE : "#6B7280",
            }}
          >
            {b.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "1.5rem 1.5rem 0.75rem" }}>
          <h3 style={{ ...sectionHeading, margin: 0 }}>
            Feedback <span style={{ color: "#9CA3AF", fontWeight: 400, fontSize: "0.85rem" }}>({filtered.length})</span>
          </h3>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["User", "Section", "Clarity", "Difficulty", "Feedback Text", "Date"].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0
                ? <EmptyRow cols={6} text="No feedback" />
                : filtered.map((row, i) => (
                  <tr key={i}>
                    <td style={td(i % 2 === 1)}>{row.userName || "—"}</td>
                    <td style={td(i % 2 === 1)}>{row.sectionTitle || "—"}</td>
                    <td style={td(i % 2 === 1)}><Stars value={row.clarityRating} /></td>
                    <td style={td(i % 2 === 1)}><DiffBadge value={row.difficulty} /></td>
                    <td style={{ ...td(i % 2 === 1), maxWidth: 260 }}>
                      <span style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}>
                        {row.feedbackText || "—"}
                      </span>
                    </td>
                    <td style={{ ...td(i % 2 === 1), whiteSpace: "nowrap" }}>
                      {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Difficulty pie */}
      {diffPie.length > 0 && (
        <div style={card}>
          <h3 style={sectionHeading}>Difficulty Breakdown</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={diffPie}
                cx="50%" cy="50%"
                outerRadius={78}
                dataKey="value"
                nameKey="name"
                label={({ name }) => name}
              >
                {diffPie.map((entry, i) => (
                  <Cell key={i} fill={DIFF_COLORS[entry.name] || "#9CA3AF"} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// ── Surveys Tab ───────────────────────────────────────────────────────────────
const LikelihoodBar = ({ score = 0 }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <div style={{
      width: 72, height: 6, background: "#E5E7EB",
      borderRadius: 99, overflow: "hidden", flexShrink: 0,
    }}>
      <div style={{
        width: `${Math.min((score / 10) * 100, 100)}%`,
        height: "100%", background: BLUE, borderRadius: 99,
      }} />
    </div>
    <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{score}/10</span>
  </div>
);

const BLOCKER_COLORS = {
  "Price":           "#EF4444",
  "Need Time":       "#D97706",
  "Not Confident":   "#8B5CF6",
  "Already Know":    "#059669",
  "Other":           "#6B7280",
};

const SurveysTab = ({ data }) => {
  const surveys      = data.purchaseIntents || [];
  const likelihood   = data.likelihoodDist || [];
  const blockerBreak = data.blockerBreakdown || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Table */}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "1.5rem 1.5rem 0.75rem" }}>
          <h3 style={{ ...sectionHeading, margin: 0 }}>
            Purchase Intent Surveys{" "}
            <span style={{ color: "#9CA3AF", fontWeight: 400, fontSize: "0.85rem" }}>({surveys.length})</span>
          </h3>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["User", "Workbook", "Likelihood", "Blocker", "Notes", "Date"].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {surveys.length === 0
                ? <EmptyRow cols={6} text="No surveys" />
                : surveys.map((row, i) => (
                  <tr key={i}>
                    <td style={td(i % 2 === 1)}>{row.userName || "—"}</td>
                    <td style={td(i % 2 === 1)}>{row.workbookTitle || "—"}</td>
                    <td style={td(i % 2 === 1)}><LikelihoodBar score={row.likelihoodScore ?? 0} /></td>
                    <td style={td(i % 2 === 1)}>{row.blocker || "—"}</td>
                    <td style={{ ...td(i % 2 === 1), maxWidth: 240 }}>
                      <span style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}>
                        {row.freeText || "—"}
                      </span>
                    </td>
                    <td style={{ ...td(i % 2 === 1), whiteSpace: "nowrap" }}>
                      {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts */}
      <div className="adm-two-col">
        {likelihood.length > 0 && (
          <div style={card}>
            <h3 style={sectionHeading}>Likelihood Distribution</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={likelihood} margin={{ top: 4, right: 8, bottom: 4, left: -16 }}>
                <XAxis dataKey="score" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill={BLUE} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {blockerBreak.length > 0 && (
          <div style={card}>
            <h3 style={sectionHeading}>Blocker Breakdown</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={blockerBreak}
                  cx="50%" cy="50%"
                  outerRadius={78}
                  dataKey="value"
                  nameKey="name"
                  label={({ name }) => name}
                >
                  {blockerBreak.map((entry, i) => (
                    <Cell key={i} fill={BLOCKER_COLORS[entry.name] || "#9CA3AF"} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main AdminDashboard ───────────────────────────────────────────────────────
// ── Resources Tab ─────────────────────────────────────────────────────────────
// Publishing screen for the free resources the lead gate hands out. Replaces
// hand-rolling two API calls per resource, which is the step that decides how
// often a new lead magnet actually ships.
//
// Everything goes through /api/admin/resources (JWT), never the [AdminKey]
// endpoints on ResourcesController: the admin key must not reach a browser.

const CATEGORIES = ["guide", "cheatsheet"];

// "CAD Automation Roadmap!" -> "cad-automation-roadmap". Mirrors
// AdminController.NormalizeSlug so the field shows what will actually be saved;
// the server normalizes again and is the authority.
const slugify = (value) =>
  (value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\-_ .]/g, "")
    .replace(/[_ .]/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");

const EMPTY_FORM = {
  id: null,
  slug: "",
  title: "",
  description: "",
  category: "guide",
  coverImageUrl: "",
  fileUrl: "",
  content: "",
  isPublished: false,
  orderIndex: 0,
};

const label = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#374151",
  marginBottom: 6,
};

const field = {
  width: "100%",
  fontSize: 13,
  padding: "9px 11px",
  borderRadius: 6,
  border: "1px solid #e8e0d0",
  background: "#fff",
  color: "#1a1a1a",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const PublishPill = ({ on }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
    background: on ? "#ECFDF5" : "#FEF3C7",
    color: on ? GREEN : "#92400E",
    border: `1px solid ${on ? "#BBF7D0" : "#FCD34D"}`,
  }}>
    {on ? "Live" : "Draft"}
  </span>
);

const ResourcesTab = ({ data, loading, error, onReload }) => {
  const [form, setForm]         = useState(EMPTY_FORM);
  const [editing, setEditing]   = useState(false);
  const [busy, setBusy]         = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg]           = useState(null);     // { kind, text }
  // The slug stops tracking the title once it has been typed in by hand, so an
  // edit to the title of a LIVE resource cannot silently change its URL and
  // break every link already shared.
  const [slugLocked, setSlugLocked] = useState(false);

  const say = (kind, text) => setMsg({ kind, text });

  const reset = () => {
    setForm(EMPTY_FORM);
    setEditing(false);
    setSlugLocked(false);
    setMsg(null);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onTitle = (v) => {
    setForm(f => ({
      ...f,
      title: v,
      slug: (!slugLocked && !editing) ? slugify(v) : f.slug,
    }));
  };

  const startEdit = async (row) => {
    setBusy(true);
    setMsg(null);
    try {
      // The list omits the HTML body, so it is fetched here rather than held in
      // memory for every row.
      const full = await apiClient.get(`/api/admin/resources/${row.id}`, { authRequired: true });
      setForm({
        id: full.id,
        slug: full.slug || "",
        title: full.title || "",
        description: full.description || "",
        category: full.category || "guide",
        coverImageUrl: full.coverImageUrl || "",
        fileUrl: full.fileUrl || "",
        content: full.content || "",
        isPublished: !!full.isPublished,
        orderIndex: full.orderIndex ?? 0,
      });
      setEditing(true);
      setSlugLocked(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      say("error", e.message || "Could not load that resource");
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (file, target = "fileUrl") => {
    if (!file) return;
    setUploading(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiClient.upload("/api/admin/resources/upload", fd, { authRequired: true });
      set(target, res.url);
      say("ok", `Uploaded ${res.fileName} (${Math.round((res.size || 0) / 1024)} KB)`);
    } catch (e) {
      say("error", e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const slug = slugify(form.slug);
    if (!slug)        return say("error", "Slug is required.");
    if (!form.title.trim()) return say("error", "Title is required.");
    // Not fatal server-side, but a resource with no file has nothing for the
    // gate to hand over, so publishing one would collect emails and give back
    // nothing. Worth stopping here.
    if (form.isPublished && !form.fileUrl.trim() && !form.content.trim())
      return say("error", "A live resource needs a PDF or an online body. Add one, or save it as a draft.");

    setBusy(true);
    setMsg(null);
    const body = {
      slug,
      title: form.title.trim(),
      description: form.description,
      category: form.category,
      coverImageUrl: form.coverImageUrl,
      fileUrl: form.fileUrl,
      content: form.content,
      isPublished: form.isPublished,
      orderIndex: Number(form.orderIndex) || 0,
    };
    try {
      if (editing) {
        await apiClient.put(`/api/admin/resources/${form.id}`, body, { authRequired: true });
        say("ok", `Saved. /resources/${slug}`);
      } else {
        await apiClient.post("/api/admin/resources", body, { authRequired: true });
        say("ok", `Created. /resources/${slug}`);
        setForm(EMPTY_FORM);
        setSlugLocked(false);
      }
      onReload();
    } catch (e) {
      say("error", e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (row) => {
    setBusy(true);
    try {
      await apiClient.put(`/api/admin/resources/${row.id}`,
        { isPublished: !row.isPublished }, { authRequired: true });
      say("ok", `${row.title} is now ${row.isPublished ? "a draft" : "live"}.`);
      onReload();
    } catch (e) {
      say("error", e.message || "Could not change that");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    // The leads already captured through this resource are NOT deleted — their
    // events keep the slug — so the warning says what actually goes.
    const sure = window.confirm(
      `Delete "${row.title}"?\n\nThe page at /resources/${row.slug} stops working. ` +
      `The ${row.leads} lead${row.leads === 1 ? "" : "s"} it captured are kept, ` +
      `and the uploaded PDF stays in storage.`
    );
    if (!sure) return;
    setBusy(true);
    try {
      await apiClient.delete(`/api/admin/resources/${row.id}`, { authRequired: true });
      say("ok", `Deleted ${row.title}.`);
      if (form.id === row.id) reset();
      onReload();
    } catch (e) {
      say("error", e.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) return (
    <div style={{ padding: "4rem 1rem", textAlign: "center", fontSize: 14 }}>
      <div style={{ color: "#EF4444", marginBottom: 12 }}>{error}</div>
      <button onClick={onReload} style={{
        fontSize: 13, padding: "8px 16px", borderRadius: 6,
        border: "1px solid #e8e0d0", background: "#fff", cursor: "pointer",
      }}>
        Try again
      </button>
    </div>
  );

  const rows = data || [];
  const live = rows.filter(r => r.isPublished).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <section>
        <h2 style={sectionHeading}>Free Resources</h2>
        <div className="adm-kpi-grid">
          <KpiCard label="Resources"  value={rows.length} />
          <KpiCard label="Live"       value={live} />
          <KpiCard label="Drafts"     value={rows.length - live} />
          <KpiCard label="Leads captured"
                   value={rows.reduce((n, r) => n + (r.leads || 0), 0)} />
        </div>
      </section>

      {/* Editor */}
      <div style={card}>
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: "1rem",
        }}>
          <h3 style={{ ...sectionHeading, margin: 0 }}>
            {editing ? `Editing: ${form.title || form.slug}` : "New resource"}
          </h3>
          {editing && (
            <button onClick={reset} style={{
              fontSize: 12, padding: "6px 12px", borderRadius: 6,
              border: "1px solid #e8e0d0", background: "#fff", cursor: "pointer",
            }}>
              Cancel, start a new one
            </button>
          )}
        </div>

        {msg && (
          <div style={{
            fontSize: 13, padding: "10px 12px", borderRadius: 6, marginBottom: "1rem",
            background: msg.kind === "ok" ? "#ECFDF5" : "#FEE2E2",
            color: msg.kind === "ok" ? "#065F46" : "#991B1B",
            border: `1px solid ${msg.kind === "ok" ? "#BBF7D0" : "#FECACA"}`,
          }}>
            {msg.text}
          </div>
        )}

        <div className="adm-two-col" style={{ marginBottom: "1rem" }}>
          <div>
            <label style={label} htmlFor="res-title">Title</label>
            <input
              id="res-title"
              style={field}
              value={form.title}
              onChange={e => onTitle(e.target.value)}
              placeholder="CAD Automation Career Roadmap"
            />
          </div>
          <div>
            <label style={label} htmlFor="res-slug">
              Slug{" "}
              <span style={{ fontWeight: 400, color: "#9CA3AF" }}>
                — the URL, and the gate's key
              </span>
            </label>
            <input
              id="res-slug"
              style={field}
              value={form.slug}
              onChange={e => { setSlugLocked(true); set("slug", e.target.value); }}
              onBlur={e => set("slug", slugify(e.target.value))}
              placeholder="cad-automation-career-roadmap"
            />
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
              {form.slug
                ? `scriptedcad.com/resources/${slugify(form.slug)}`
                : "Fills in from the title"}
              {editing && " — changing this breaks links already shared"}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label style={label} htmlFor="res-desc">Description</label>
          <input
            id="res-desc"
            style={field}
            value={form.description}
            onChange={e => set("description", e.target.value)}
            placeholder="One line. Shown on the cards and under the title."
          />
        </div>

        {/* PDF */}
        <div style={{ marginBottom: "1rem" }}>
          <label style={label}>PDF</label>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.svg,.zip"
              disabled={uploading || busy}
              onChange={e => onFile(e.target.files && e.target.files[0])}
              style={{ fontSize: 13 }}
            />
            {uploading && <span style={{ fontSize: 12, color: "#6B7280" }}>Uploading…</span>}
          </div>
          <input
            style={{ ...field, marginTop: 8, fontSize: 12, color: "#6B7280" }}
            value={form.fileUrl}
            onChange={e => set("fileUrl", e.target.value)}
            placeholder="…or paste a file URL directly"
          />
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
            Export the PDF from Notion, then drop it here. This URL is never put
            in the page HTML — the gate hands it over after an email.
          </div>
        </div>

        {/* Cover image. Same uploader as the PDF — the endpoint takes images
            too — so a cover does not need a separate storage trip. */}
        <div style={{ marginBottom: "1rem" }}>
          <label style={label} htmlFor="res-cover">
            Cover image{" "}
            <span style={{ fontWeight: 400, color: "#9CA3AF" }}>— optional</span>
          </label>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.svg"
              disabled={uploading || busy}
              onChange={e => onFile(e.target.files && e.target.files[0], "coverImageUrl")}
              style={{ fontSize: 13 }}
            />
          </div>
          <input
            id="res-cover"
            style={{ ...field, marginTop: 8, fontSize: 12, color: "#6B7280" }}
            value={form.coverImageUrl}
            onChange={e => set("coverImageUrl", e.target.value)}
            placeholder="…or paste an image URL"
          />
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
            Shown on the resource cards at /resources.
          </div>
        </div>

        {/* Online body */}
        <div style={{ marginBottom: "1rem" }}>
          <label style={label} htmlFor="res-content">
            Online body{" "}
            <span style={{ fontWeight: 400, color: "#9CA3AF" }}>— HTML</span>
          </label>
          <textarea
            id="res-content"
            style={{ ...field, minHeight: 200, fontFamily: "ui-monospace, monospace", fontSize: 12 }}
            value={form.content}
            onChange={e => set("content", e.target.value)}
            placeholder={'<h2>What is CAD automation?</h2>\n<p>…</p>\n<div class="rr-callout">A note worth pulling out.</div>\n<pre class="rr-code">var swApp = ...</pre>'}
          />
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
            This is what Google reads, so a resource with a PDF but no body ranks
            for nothing. <code>rr-callout</code> and <code>rr-code</code> are styled
            for you. Rendered as raw HTML — only paste what you wrote.
          </div>
        </div>

        <div className="adm-two-col" style={{ marginBottom: "1.25rem" }}>
          <div>
            <label style={label} htmlFor="res-cat">Category</label>
            <select
              id="res-cat"
              style={field}
              value={form.category}
              onChange={e => set("category", e.target.value)}
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="res-order">
              Order{" "}
              <span style={{ fontWeight: 400, color: "#9CA3AF" }}>— low shows first</span>
            </label>
            <input
              id="res-order"
              type="number"
              style={field}
              value={form.orderIndex}
              onChange={e => set("orderIndex", e.target.value)}
            />
          </div>
        </div>

        <label style={{
          display: "flex", gap: 8, alignItems: "center",
          fontSize: 13, color: "#374151", marginBottom: "1.25rem", cursor: "pointer",
        }}>
          <input
            type="checkbox"
            checked={form.isPublished}
            onChange={e => set("isPublished", e.target.checked)}
          />
          Publish this resource — visible on /resources and downloadable
        </label>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={save}
            disabled={busy || uploading}
            style={{
              fontSize: 13, fontWeight: 600, padding: "10px 20px", borderRadius: 6,
              border: `1px solid ${BLUE}`, background: BLUE, color: "#fff",
              cursor: busy || uploading ? "default" : "pointer",
              opacity: busy || uploading ? 0.6 : 1,
            }}
          >
            {busy ? "Saving…" : editing ? "Save changes" : "Create resource"}
          </button>
          {form.slug && form.isPublished && (
            <a
              href={`https://scriptedcad.com/resources/${slugify(form.slug)}`}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 13, padding: "10px 16px", borderRadius: 6,
                border: "1px solid #e8e0d0", background: "#fff",
                color: "#374151", textDecoration: "none",
              }}
            >
              View live page ↗
            </a>
          )}
        </div>
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 10 }}>
          Changes clear the server cache at once, but the CDN holds its copy for
          up to 5 minutes, so the public page can lag behind this screen.
        </div>
      </div>

      {/* List */}
      <div style={card}>
        <h3 style={{ ...sectionHeading, margin: "0 0 1rem" }}>All resources</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Resource</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>PDF</th>
                <th style={thStyle}>Body</th>
                <th style={thStyle}>Leads</th>
                <th style={thStyle}>Downloads</th>
                <th style={thStyle}>Order</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0
                ? <EmptyRow cols={9} text="No resources yet — create your first one above" />
                : rows.map((r, i) => (
                  <tr key={r.id}>
                    <td style={td(i % 2 === 1)}>
                      <div style={{ fontWeight: 600, color: "#1a1a1a" }}>{r.title}</div>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>/{r.slug}</div>
                    </td>
                    <td style={td(i % 2 === 1)}><PublishPill on={r.isPublished} /></td>
                    <td style={td(i % 2 === 1)}>{r.category}</td>
                    <td style={td(i % 2 === 1)}>{r.fileUrl ? "Yes" : "—"}</td>
                    <td style={td(i % 2 === 1)}>
                      {r.hasContent
                        ? `${Math.round((r.contentLength || 0) / 100) / 10}k`
                        : <span style={{ color: "#D97706" }} title="No online body — nothing for Google to index">None</span>}
                    </td>
                    <td style={td(i % 2 === 1)}>{r.leads}</td>
                    <td style={td(i % 2 === 1)}>{r.downloads}</td>
                    <td style={td(i % 2 === 1)}>{r.orderIndex}</td>
                    <td style={td(i % 2 === 1)}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button
                          onClick={() => startEdit(r)}
                          disabled={busy}
                          style={{
                            fontSize: 11, padding: "4px 10px", borderRadius: 5,
                            border: "1px solid #e8e0d0", background: "#fff", cursor: "pointer",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => togglePublish(r)}
                          disabled={busy}
                          style={{
                            fontSize: 11, padding: "4px 10px", borderRadius: 5,
                            border: "1px solid #e8e0d0", background: "#fff", cursor: "pointer",
                          }}
                        >
                          {r.isPublished ? "Unpublish" : "Publish"}
                        </button>
                        <button
                          onClick={() => remove(r)}
                          disabled={busy}
                          style={{
                            fontSize: 11, padding: "4px 10px", borderRadius: 5,
                            border: "1px solid #FECACA", background: "#fff",
                            color: "#991B1B", cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Leads Tab ─────────────────────────────────────────────────────────────────
// The top of the funnel: everyone who handed over an email at a resource gate,
// whether or not they ever registered. Fed by /api/admin/leads, which is a
// separate request from /api/admin/dashboard so this tab's cost is only paid
// when it is opened.

const SEGMENT_STYLE = {
  hot:  { label: "Hot",  bg: "#FEE2E2", color: "#991B1B", border: "#FECACA" },
  warm: { label: "Warm", bg: "#FEF3C7", color: "#92400E", border: "#FCD34D" },
  cold: { label: "Cold", bg: "#F3F4F6", color: "#6B7280", border: "#E5E7EB" },
};

const SegmentBadge = ({ value }) => {
  const s = SEGMENT_STYLE[value] || SEGMENT_STYLE.cold;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {s.label}
    </span>
  );
};

// Registered on the platform, which is not the same as having paid — so this
// deliberately does not reuse TierBadge's Paid/Free wording.
const UserBadge = ({ value }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
    background: value ? "#EFF6FF" : "#F3F4F6",
    color: value ? "#1D4ED8" : "#9CA3AF",
    border: `1px solid ${value ? "#BFDBFE" : "#E5E7EB"}`,
  }}>
    {value ? "Signed up" : "Lead only"}
  </span>
);

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" });
};

const LeadsTab = ({ data, loading, error, onRetry }) => {
  // Default to hot so the first thing on screen is the follow-up queue, not a
  // wall of cold leads. "all" is one click away.
  const [segment, setSegment] = useState("all");
  const [query, setQuery]     = useState("");

  if (loading) return <Spinner />;
  if (error) return (
    <div style={{ padding: "4rem 1rem", textAlign: "center", fontSize: 14 }}>
      <div style={{ color: "#EF4444", marginBottom: 12 }}>{error}</div>
      <button onClick={onRetry} style={{
        fontSize: 13, padding: "8px 16px", borderRadius: 6,
        border: "1px solid #e8e0d0", background: "#fff", cursor: "pointer",
      }}>
        Try again
      </button>
    </div>
  );
  if (!data) return null;

  const k = data.kpis || {};
  const all = data.leads || [];

  const rows = all.filter(l => {
    if (segment !== "all" && l.segment !== segment) return false;
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return [l.email, l.name, l.source, l.utmCampaign, l.role, l.cadTool]
      .some(v => (v || "").toLowerCase().includes(q));
  });

  // Of everyone who gave an email, the share that came back for a second
  // resource. The one number on this page that says whether the content is
  // earning trust rather than just reach.
  const returnRate = k.totalLeads
    ? Math.round((k.returning / k.totalLeads) * 100)
    : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* KPI Grid */}
      <section>
        <h2 style={sectionHeading}>Lead Capture</h2>
        <div className="adm-kpi-grid">
          <KpiCard label="Total leads"            value={k.totalLeads} />
          <KpiCard label="New (last 7 days)"      value={k.newLast7} />
          <KpiCard label="New (last 30 days)"     value={k.newLast30} />
          <KpiCard label="Total downloads"        value={k.totalDownloads} />
        </div>
      </section>

      <section>
        <h2 style={sectionHeading}>Quality</h2>
        <div className="adm-kpi-grid">
          <KpiCard label="Hot leads"                   value={k.hot} />
          <KpiCard label="Warm leads"                  value={k.warm} />
          <KpiCard label={`Came back for a 2nd (${returnRate}%)`} value={k.returning} />
          <KpiCard label="WhatsApp opt-ins"            value={k.withWhatsapp} />
        </div>
      </section>

      {/* Charts */}
      <div className="adm-two-col">
        <div style={card}>
          <h3 style={sectionHeading}>Leads per Week</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.leadsPerWeek || []}>
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill={BLUE} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={card}>
          <h3 style={sectionHeading}>Segment Split</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={data.segmentSplit || []}
                dataKey="value"
                nameKey="name"
                cx="50%" cy="50%"
                outerRadius={70}
                label
              >
                {/* Colour carries the same meaning as the badges in the table,
                    so the two read as one thing. */}
                {(data.segmentSplit || []).map(s => (
                  <Cell
                    key={s.name}
                    fill={s.name === "hot" ? "#DC2626" : s.name === "warm" ? AMBER : "#9CA3AF"}
                  />
                ))}
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Where leads come from */}
      <div style={card}>
        <h3 style={{ ...sectionHeading, margin: 0 }}>By Source</h3>
        <p style={{ fontSize: 12, color: "#6B7280", margin: "4px 0 1rem" }}>
          First touch: the channel that found each person. Leads captured before
          campaign tagging existed show as <em>untagged</em>.
        </p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Source</th>
                <th style={thStyle}>Leads</th>
                <th style={thStyle}>Hot</th>
                <th style={thStyle}>Warm</th>
              </tr>
            </thead>
            <tbody>
              {(data.bySource || []).length === 0
                ? <EmptyRow cols={4} text="No leads yet" />
                : (data.bySource || []).map((r, i) => (
                  <tr key={r.source}>
                    <td style={td(i % 2 === 1)}>{r.source}</td>
                    <td style={td(i % 2 === 1)}>{r.leads}</td>
                    <td style={td(i % 2 === 1)}>{r.hot}</td>
                    <td style={td(i % 2 === 1)}>{r.warm}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Which campaign / post pulled them in */}
      {(data.byCampaign || []).length > 0 && (
        <div style={card}>
          <h3 style={{ ...sectionHeading, margin: 0 }}>By Campaign</h3>
          <p style={{ fontSize: 12, color: "#6B7280", margin: "4px 0 1rem" }}>
            Per post, from the <code>utm_campaign</code> tag on the link you shared.
          </p>
          <ResponsiveContainer width="100%" height={Math.max(180, (data.byCampaign.length) * 34)}>
            <BarChart data={data.byCampaign} layout="vertical">
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="campaign" width={150} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="leads" fill={GREEN} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Which resource is doing the work */}
      <div style={card}>
        <h3 style={{ ...sectionHeading, margin: 0 }}>Per Resource</h3>
        <p style={{ fontSize: 12, color: "#6B7280", margin: "4px 0 1rem" }}>
          Downloads counts every unlock; unique leads counts people. A big gap
          means the same few people keep coming back.
        </p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Resource</th>
                <th style={thStyle}>Downloads</th>
                <th style={thStyle}>Unique leads</th>
              </tr>
            </thead>
            <tbody>
              {(data.perResource || []).length === 0
                ? <EmptyRow cols={3} text="No downloads yet" />
                : (data.perResource || []).map((r, i) => (
                  <tr key={r.slug}>
                    <td style={td(i % 2 === 1)}>{r.title}</td>
                    <td style={td(i % 2 === 1)}>{r.downloads}</td>
                    <td style={td(i % 2 === 1)}>{r.uniqueLeads}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* The list itself — ordered hot first, which makes it the work queue */}
      <div style={card}>
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 12,
          alignItems: "center", justifyContent: "space-between", marginBottom: "1rem",
        }}>
          <h3 style={{ ...sectionHeading, margin: 0 }}>
            Leads ({rows.length}{rows.length !== all.length ? ` of ${all.length}` : ""})
          </h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["all", "hot", "warm", "cold"].map(s => (
              <button
                key={s}
                onClick={() => setSegment(s)}
                style={{
                  fontSize: 12, padding: "6px 12px", borderRadius: 99, cursor: "pointer",
                  textTransform: "capitalize",
                  border: `1px solid ${segment === s ? BLUE : "#e8e0d0"}`,
                  background: segment === s ? BLUE : "#fff",
                  color: segment === s ? "#fff" : "#374151",
                }}
              >
                {s}
              </button>
            ))}
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search email, name, source…"
              style={{
                fontSize: 13, padding: "6px 10px", borderRadius: 6,
                border: "1px solid #e8e0d0", minWidth: 180,
              }}
            />
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Lead</th>
                <th style={thStyle}>Segment</th>
                <th style={thStyle}>Score</th>
                <th style={thStyle}>WhatsApp</th>
                <th style={thStyle}>Source</th>
                <th style={thStyle}>Files</th>
                <th style={thStyle}>Role / Tool</th>
                <th style={thStyle}>Goal</th>
                <th style={thStyle}>First seen</th>
                <th style={thStyle}>Last active</th>
                <th style={thStyle}>Registered</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0
                ? <EmptyRow cols={11} text="No leads match this filter" />
                : rows.map((l, i) => (
                  <tr key={l.id}>
                    <td style={td(i % 2 === 1)}>
                      <div style={{ fontWeight: 600, color: "#1a1a1a" }}>{l.name || "—"}</div>
                      <div style={{ fontSize: 12, color: "#6B7280" }}>{l.email}</div>
                    </td>
                    <td style={td(i % 2 === 1)}><SegmentBadge value={l.segment} /></td>
                    <td style={td(i % 2 === 1)}>{l.score}</td>
                    <td style={td(i % 2 === 1)}>
                      {l.phone
                        ? (
                          // Opted-in numbers are a click-to-chat link, because the
                          // point of this table is to start the conversation. A
                          // number without opt-in is shown but deliberately not
                          // linked.
                          l.whatsappOptIn
                            ? <a
                                href={`https://wa.me/${l.phone.replace(/[^0-9]/g, "")}`}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: GREEN, fontWeight: 600, textDecoration: "none" }}
                              >
                                {l.phone}
                              </a>
                            : <span title="No opt-in — do not message">{l.phone}</span>
                        )
                        : "—"}
                    </td>
                    <td style={td(i % 2 === 1)}>
                      <div>{l.source}</div>
                      {l.utmCampaign && (
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>{l.utmCampaign}</div>
                      )}
                    </td>
                    <td style={td(i % 2 === 1)}>
                      {l.distinctResources}
                      {l.downloads !== l.distinctResources && (
                        <span style={{ fontSize: 11, color: "#9CA3AF" }}> ({l.downloads})</span>
                      )}
                    </td>
                    <td style={td(i % 2 === 1)}>
                      <div>{l.role || "—"}</div>
                      {l.cadTool && (
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>{l.cadTool}</div>
                      )}
                    </td>
                    <td style={td(i % 2 === 1)}>
                      <div>{l.goal || "—"}</div>
                      {l.timeline && (
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>{l.timeline}</div>
                      )}
                    </td>
                    <td style={td(i % 2 === 1)}>{fmtDate(l.firstSeenAt)}</td>
                    <td style={td(i % 2 === 1)}>{fmtDate(l.lastActiveAt)}</td>
                    <td style={td(i % 2 === 1)}><UserBadge value={l.isUser} /></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [email, setEmail]             = useState("");
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [activeTab, setActiveTab]     = useState("overview");

  // Leads live behind their own endpoint and are fetched the first time the tab
  // is opened. leadsLoading starts false so the tab is not stuck on a spinner
  // before anything has been requested.
  const [leads, setLeads]               = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError]     = useState(null);

  const [resources, setResources]           = useState(null);
  const [resourcesLoading, setResLoading]   = useState(false);
  const [resourcesError, setResError]       = useState(null);

  const loadResources = useCallback(() => {
    setResLoading(true);
    setResError(null);
    apiClient.get("/api/admin/resources", { authRequired: true })
      .then(d => { setResources(d); setResLoading(false); })
      .catch(e => { setResError(e.message || "Failed to load resources"); setResLoading(false); });
  }, []);

  const loadLeads = useCallback(() => {
    setLeadsLoading(true);
    setLeadsError(null);
    apiClient.get("/api/admin/leads", { authRequired: true })
      .then(d => { setLeads(d); setLeadsLoading(false); })
      .catch(e => { setLeadsError(e.message || "Failed to load leads"); setLeadsLoading(false); });
  }, []);

  // Auth guard
  useEffect(() => {
    const token = localStorage.getItem("sc_token");
    const raw   = localStorage.getItem("sc_user");
    // replace on all three: these are redirects caused by state, and pushing
    // left the back button pointing at a page that only redirects again. No
    // ?next= here, because coming back is exactly what must not happen.
    if (!token || !raw) { navigate("/", { replace: true }); return; }
    try {
      const user = JSON.parse(raw);
      if (user.email !== "founder@scriptedcad.com") { navigate("/", { replace: true }); return; }
      setEmail(user.email);
      setAuthChecked(true);
    } catch {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Data fetch
  useEffect(() => {
    if (!authChecked) return;
    apiClient.get("/api/admin/dashboard", { authRequired: true })
      .then(d  => { console.log("[AdminDashboard] response:", d); setData(d); setLoading(false); })
      .catch(e => { console.error("[AdminDashboard] error:", e); setError(e.message || "Failed to load dashboard"); setLoading(false); });
  }, [authChecked]);

  useEffect(() => {
    if (!authChecked) return;
    if (activeTab !== "resources") return;
    if (resources || resourcesLoading || resourcesError) return;
    loadResources();
  }, [authChecked, activeTab, resources, resourcesLoading, resourcesError, loadResources]);

  // Fetched once, on first open. Re-entering the tab reuses what is held; the
  // tab's own Try again button is the way to refresh.
  useEffect(() => {
    if (!authChecked) return;
    if (activeTab !== "leads") return;
    if (leads || leadsLoading || leadsError) return;
    loadLeads();
  }, [authChecked, activeTab, leads, leadsLoading, leadsError, loadLeads]);

  if (!authChecked) return null;

  const renderTab = () => {
    if (loading && activeTab !== "leads" && activeTab !== "resources") return <Spinner />;
    if (error   && activeTab !== "leads" && activeTab !== "resources") return (
      <div style={{ padding: "4rem 1rem", textAlign: "center", color: "#EF4444", fontSize: 14 }}>
        {error}
      </div>
    );
    if (activeTab === "resources") return (
      <ResourcesTab
        data={resources}
        loading={resourcesLoading}
        error={resourcesError}
        onReload={loadResources}
      />
    );
    if (activeTab === "leads") return (
      <LeadsTab
        data={leads}
        loading={leadsLoading}
        error={leadsError}
        onRetry={loadLeads}
      />
    );
    if (!data)   return null;
    switch (activeTab) {
      case "overview":  return <OverviewTab  data={data} />;
      case "users":     return <UsersTab     users={data.users} />;
      case "workbooks": return <WorkbooksTab data={data} />;
      case "feedback":  return <FeedbackTab  data={data} />;
      case "surveys":   return <SurveysTab   data={data} />;
      default:          return null;
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F5F2EC", fontFamily: "Inter, sans-serif" }}>

      {/* Injected responsive styles */}
      <style>{`
        .adm-kpi-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }
        @media (min-width: 768px) {
          .adm-kpi-grid { grid-template-columns: repeat(4, 1fr); }
        }
        .adm-two-col {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .adm-two-col { grid-template-columns: 1fr 1fr; }
        }
        .adm-wb-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 0;
        }
        .adm-sidebar     { display: none !important; }
        .adm-bottom-tabs { display: flex !important; }
        @media (min-width: 768px) {
          .adm-sidebar     { display: flex !important; }
          .adm-bottom-tabs { display: none !important; }
        }
        .adm-user-row:hover td { background: #EFF6FF !important; }
        .adm-sidebar-btn:hover { background: #F5F2EC !important; }
        @keyframes adm-spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* ── Sticky Navbar ── */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "#fff", borderBottom: "1px solid #e8e0d0",
        padding: "0 1.5rem", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <span style={{
          fontFamily: "'Fraunces', serif", fontWeight: 700,
          fontSize: "1.1rem", color: "#1a1a1a",
        }}>
          ScriptedCAD Admin
        </span>
        <span style={{ fontSize: 12, color: "#6B7280" }}>{email}</span>
      </nav>

      <div style={{ display: "flex", minHeight: "calc(100vh - 56px)" }}>
        {/* ── Desktop Sidebar ── */}
        <aside
          className="adm-sidebar"
          style={{
            width: 200, background: "#fff", borderRight: "1px solid #e8e0d0",
            flexDirection: "column", padding: "1.25rem 0", flexShrink: 0,
          }}
        >
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                className="adm-sidebar-btn"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  width: "100%", padding: "10px 1.25rem",
                  display: "flex", alignItems: "center", gap: 10,
                  border: "none", cursor: "pointer", textAlign: "left",
                  fontFamily: "inherit", fontSize: 14,
                  fontWeight: active ? 600 : 400,
                  background: active ? "#EFF6FF" : "transparent",
                  color: active ? BLUE : "#374151",
                  borderLeft: `3px solid ${active ? BLUE : "transparent"}`,
                  transition: "background 0.15s",
                }}
              >
                <span style={{ fontSize: 16 }}>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </aside>

        {/* ── Main Content ── */}
        <main style={{
          flex: 1, padding: "1.5rem",
          paddingBottom: 80,
          overflowX: "hidden",
          minWidth: 0,
        }}>
          {renderTab()}
        </main>
      </div>

      {/* ── Mobile Bottom Tab Bar ── */}
      <nav
        className="adm-bottom-tabs"
        style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
          background: "#fff", borderTop: "1px solid #e8e0d0",
          justifyContent: "space-around", alignItems: "center", height: 60,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 2, border: "none", background: "transparent",
                cursor: "pointer", fontFamily: "inherit", padding: "6px 0",
                color: active ? BLUE : "#9CA3AF",
              }}
            >
              <span style={{ fontSize: 18 }}>{tab.icon}</span>
              <span style={{ fontSize: 10, fontWeight: active ? 600 : 400 }}>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default AdminDashboard;
